import { Card, Grow, Skeleton } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";

import { DashboardColor, pulseSx, staggerDelay, surfaceCardSx } from "../styles";

type RankingItem = {
  label: string;
  value: number;
  caption?: string;
};

type Props = {
  title: string;
  items: RankingItem[];
  color?: DashboardColor;
  emptyLabel: string;
  isLoading?: boolean;
  isRefreshing?: boolean;
};

const RankingCard = ({
  title,
  items,
  color = "info",
  emptyLabel,
  isLoading = false,
  isRefreshing = false,
}: Props) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <Card sx={surfaceCardSx(color)}>
      <MDBox p={2}>
        <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
          {title}
        </MDTypography>
        {isLoading ? (
          <>
            {[0, 1, 2, 3].map((item) => (
              <MDBox key={item} mb={1.5}>
                <Skeleton variant="text" width="55%" height={16} />
                <Skeleton variant="rounded" width="100%" height={10} />
              </MDBox>
            ))}
          </>
        ) : items.length === 0 ? (
          <MDTypography variant="button" color="text">
            {emptyLabel}
          </MDTypography>
        ) : (
          <MDBox sx={pulseSx(isRefreshing)}>
            {items.map((item, index) => (
              <Grow in timeout={520} style={{ transitionDelay: staggerDelay(index) }} key={item.label}>
                <MDBox mb={1.25}>
                  <MDBox display="flex" justifyContent="space-between" mb={0.5} gap={1}>
                    <MDTypography
                      variant="caption"
                      fontWeight={index === 0 ? "bold" : "medium"}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {index === 0 ? "★ " : ""}
                      {item.label}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      textAlign="right"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {item.caption ?? item.value}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    width="100%"
                    height="0.375rem"
                    borderRadius="lg"
                    bgColor="light"
                    overflow="hidden"
                  >
                    <MDBox
                      variant="gradient"
                      bgColor={index === 0 ? color : "light"}
                      height="100%"
                      borderRadius="lg"
                      width={`${Math.min(100, (item.value / maxValue) * 100)}%`}
                      sx={(theme) => ({
                        transition: "width 1s cubic-bezier(0.22, 1, 0.36, 1)",
                        ...(index === 0
                          ? {}
                          : {
                              backgroundImage: `linear-gradient(90deg, ${
                                (theme.palette as any)[color]?.main ?? theme.palette.primary.main
                              }bb, ${(theme.palette as any)[color]?.main ?? theme.palette.primary.main}55)`,
                            }),
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                      })}
                    />
                  </MDBox>
                </MDBox>
              </Grow>
            ))}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

export default RankingCard;
